import React, { useState } from "react";
import {
  MoreVertical,
  Edit2,
  Trash2,
  Plus,
  Calculator,
  ChevronUp,
  ChevronDown,
  Grip,
} from "lucide-react";
import ColumnHeader from "./ColumnHeader";
import CellRenderer from "./CellRenderer";

export default function DataTable({
  columns,
  rows,
  aggregations,
  permission,
  onEditColumn,
  onDeleteColumn,
  onEditRow,
  onDeleteRow,
  onCreateAggregation,
  onDeleteAggregation,
  startIndex = 0, // For pagination
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);

  const canEdit =
    permission === "owner" || permission === "admin" || permission === "edit";
  const canManage = permission === "owner" || permission === "admin";

  // Filter out ID column completely
  const visibleColumns = columns.filter((col) => col.column_key !== "id");

  // System columns that are always shown
  const systemColumns = ["created_at", "updated_at"];

  // Combine system columns with user columns
  const allColumns = [
    ...visibleColumns.filter((col) => systemColumns.includes(col.column_key)),
    ...visibleColumns.filter((col) => !systemColumns.includes(col.column_key)),
  ];

  // Sort rows
  const sortedRows = [...rows].sort((a, b) => {
    if (!sortConfig.key) return 0;

    // Handle index sorting
    if (sortConfig.key === "_index") {
      const aIndex = rows.indexOf(a);
      const bIndex = rows.indexOf(b);
      return sortConfig.direction === "asc" ? aIndex - bIndex : bIndex - aIndex;
    }

    const aValue = a.data[sortConfig.key];
    const bValue = b.data[sortConfig.key];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;
    if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const startResizing = (columnId, startX) => {
    setResizingColumn({
      columnId,
      startX,
      startWidth: columnWidths[columnId] || 150,
    });
  };

  React.useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - resizingColumn.startX;
      const newWidth = Math.max(80, resizingColumn.startWidth + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn.columnId]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizingColumn]);

  // Get aggregation value for a column
  const getAggregationValue = (columnId) => {
    const agg = aggregations.find((a) => a.column_id === columnId);
    return agg?.value;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {/* Row Number Column */}
              <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  onClick={() => handleSort("_index")}
                  className="flex items-center gap-1 hover:text-gray-700 transition-colors"
                >
                  <span>번호</span>
                  <div className="ml-1">
                    {sortConfig.key === "_index" ? (
                      sortConfig.direction === "asc" ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )
                    ) : (
                      <div className="w-3 h-3" />
                    )}
                  </div>
                </button>
              </th>

              {allColumns.map((column) => (
                <ColumnHeader
                  key={column.id}
                  column={column}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  onEdit={canManage ? () => onEditColumn(column) : null}
                  onDelete={
                    canManage && !systemColumns.includes(column.column_key)
                      ? () => onDeleteColumn(column.id)
                      : null
                  }
                  width={columnWidths[column.id] || column.width || 150}
                  onResize={(startX) => startResizing(column.id, startX)}
                />
              ))}

              {canEdit && (
                <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  작업
                </th>
              )}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRows.map((row, rowIndex) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {/* Row Number */}
                <td className="px-4 py-3 text-sm font-medium text-gray-600">
                  {startIndex + rowIndex + 1}
                </td>

                {allColumns.map((column) => (
                  <td
                    key={column.id}
                    className="px-4 py-3 text-sm"
                    style={{
                      width: columnWidths[column.id] || column.width || 150,
                    }}
                  >
                    <CellRenderer
                      column={column}
                      value={row.data[column.column_key]}
                      isEditable={
                        canEdit && !systemColumns.includes(column.column_key)
                      }
                    />
                  </td>
                ))}

                {canEdit && (
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onEditRow(row)}
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="수정"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => onDeleteRow(row.id)}
                        className="p-1.5 hover:bg-red-50 rounded transition-colors"
                        title="삭제"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>

          {/* Aggregations Row */}
          {aggregations.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-4 py-3"></td>

                {allColumns.map((column) => {
                  const aggValue = getAggregationValue(column.id);
                  const aggregation = aggregations.find(
                    (a) => a.column_id === column.id
                  );

                  return (
                    <td
                      key={column.id}
                      className="px-4 py-3 text-sm font-medium"
                    >
                      {aggValue !== undefined && (
                        <div className="flex items-center gap-2">
                          <Calculator className="w-3 h-3 text-gray-500" />
                          <span className="text-gray-700">
                            {aggregation?.display_name}:{" "}
                            {typeof aggValue === "number"
                              ? aggValue.toLocaleString()
                              : aggValue}
                          </span>
                        </div>
                      )}
                    </td>
                  );
                })}

                {canEdit && <td className="px-4 py-3"></td>}
              </tr>
            </tfoot>
          )}
        </table>

        {rows.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">데이터가 없습니다.</p>
            {canEdit && (
              <p className="text-sm text-gray-400 mt-2">
                상단의 "데이터 추가" 버튼을 클릭하여 새로운 데이터를 추가하세요.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
